import { Component } from '@theme/component';

/**
 * @typedef {object} TabsRefs
 * @property {HTMLElement[]} tabs - The tab buttons.
 * @property {HTMLElement[]} panels - The tab panels.
 *
 * @extends Component<TabsRefs>
 */
class TabsComponent extends Component {
  #active = 0;
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    const { tabs, panels } = this.refs;
    if (!tabs || tabs.length === 0) return;

    // Sync tab labels from panel data attributes
    this.#syncTabLabels(tabs, panels);

    // Find the initially active tab
    this.#active = Math.max(
      0,
      tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true')
    );

    this.#setupEventListeners();
    this.#updateActiveTab();
  }

  /**
   * Syncs tab button labels from panel data attributes
   * @param {HTMLElement[]} tabs
   * @param {HTMLElement[]} panels
   */
  #syncTabLabels(tabs, panels) {
    if (!tabs || !panels) return;

    for (const tab of tabs) {
      const panelId = tab.getAttribute('data-panel-id');
      if (!panelId) continue;

      const panel = panels.find((p) => p.id === panelId);
      if (panel) {
        const label = panel.getAttribute('data-tab-label');
        if (label) {
          tab.textContent = label;
        }
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  #setupEventListeners() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };
    const { tabs } = this.refs;

    if (!tabs || tabs.length === 0) return;

    this.addEventListener('keydown', (e) => this.#handleKeydown(e), opts);

    for (const [i, tab] of tabs.entries()) {
      tab.addEventListener('click', (e) => this.#handleTabClick(e, i), opts);
    }
  }

  /**
   * Handles keyboard navigation for tabs
   * @param {KeyboardEvent} e
   */
  #handleKeydown(e) {
    const target = /** @type {HTMLElement} */ (e.target);
    if (target.getAttribute('role') !== 'tab') return;

    const { tabs } = this.refs;
    if (!tabs) return;

    const i = tabs.indexOf(target);
    /** @type {Record<string, number>} */
    const navMap = {
      ArrowLeft: -1,
      ArrowRight: 1,
      Home: -i,
      End: tabs.length - 1 - i,
    };

    const offset = navMap[e.key];
    if (offset !== undefined) {
      e.preventDefault();
      const nextIndex = (i + offset + tabs.length) % tabs.length;
      tabs[nextIndex]?.focus();
      this.#activate(nextIndex);
    }
  }

  /**
   * Handles tab click
   * @param {MouseEvent} e
   * @param {number} index
   */
  #handleTabClick(e, index) {
    e.preventDefault();
    this.#activate(index);
  }

  /**
   * Activates a tab by index
   * @param {number} index
   */
  #activate(index) {
    const { tabs } = this.refs;
    if (!tabs || index === this.#active || index < 0 || index >= tabs.length) return;

    this.#active = index;
    this.#updateActiveTab();
  }

  /**
   * Updates ARIA attributes and visibility of tabs and panels
   */
  #updateActiveTab() {
    const { tabs, panels } = this.refs;

    for (const [i, tab] of tabs?.entries() ?? []) {
      const isActive = i === this.#active;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [i, panel] of panels?.entries() ?? []) {
      const isActive = i === this.#active;
      panel.toggleAttribute('inert', !isActive);
      panel.setAttribute('tabindex', isActive ? '0' : '-1');
    }
  }
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}
